/**
 * DesktopStyleManager - Platform-Scoped CSS Management
 * Prevents mobile CSS contamination on desktop
 * Enterprise-grade style isolation and management
 */

// Dynamic import to avoid module loading issues
let MobileDetectionService;

export class DesktopStyleManager {
    constructor() {
        this.appliedStyles = new Set();
        this.blockedMobileStyles = new Set();
        this.styleObserver = null;
        
        console.log('🖥️ DesktopStyleManager initialized');
        
        // CRITICAL: Immediate desktop protection based on fallback detection
        // Don't wait for MobileDetectionService - protect immediately
        this.isDesktop = !(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
        
        if (this.isDesktop) {
            console.log('🛡️ Applying immediate desktop protection (fallback detection)');
            this.initializeDesktopProtection();
        }
        
        // Then enhance with proper MobileDetectionService when available
        this.loadMobileDetectionService().then(() => {
            const properDetection = !MobileDetectionService?.isMobile();
            if (properDetection !== this.isDesktop) {
                console.log(`🔄 Updating desktop detection: ${this.isDesktop} → ${properDetection}`);
                this.isDesktop = properDetection;
                if (this.isDesktop && !this.appliedStyles.has('desktop-style-manager')) {
                    this.initializeDesktopProtection();
                }
            }
        });
    }

    /**
     * Load MobileDetectionService dynamically
     */
    async loadMobileDetectionService() {
        try {
            if (window.MobileDetectionService) {
                MobileDetectionService = window.MobileDetectionService;
            } else {
                const mobileModule = await import('./MobileDetectionService.js');
                MobileDetectionService = mobileModule.default;
            }
        } catch (error) {
            console.warn('⚠️ MobileDetectionService not available:', error.message);
            // Fallback detection
            this.isDesktop = !(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
        }
    }

    /**
     * Initialize desktop CSS protection
     */
    initializeDesktopProtection() {
        console.log('🛡️ Initializing desktop CSS protection');
        
        // Block mobile-specific CSS from affecting desktop
        this.blockMobileContamination();
        
        // Apply desktop-specific overrides
        this.applyDesktopStyles();
        
        // Monitor for dynamic mobile style injection
        this.setupStyleMonitoring();
        
        console.log('✅ Desktop CSS protection active');
    }

    /**
     * Block mobile CSS contamination
     */
    blockMobileContamination() {
        // List of mobile CSS files that should not affect desktop
        const mobileStyleFiles = [
            'mobile-styles.css',
            'unified-mobile.css'
        ];
        
        // Find and neutralize mobile stylesheets for desktop
        const stylesheets = document.querySelectorAll('link[rel="stylesheet"]');
        stylesheets.forEach(stylesheet => {
            const href = stylesheet.href || '';
            const isMobileStyle = mobileStyleFiles.some(file => href.includes(file));
            
            if (isMobileStyle) {
                console.log(`🚫 Neutralizing mobile CSS for desktop: ${href}`);
                
                // Add desktop-only media query to prevent mobile styles from applying
                stylesheet.media = 'screen and (max-width: 0px)'; // Never matches
                this.blockedMobileStyles.add(stylesheet);
            }
        });
    }

    /**
     * Apply desktop-specific styles and overrides
     */
    applyDesktopStyles() {
        if (!this.isDesktop) return;
        
        const desktopStyles = `
            /* Desktop Style Manager - CSS Contamination Prevention */
            /* Applied only on desktop to override mobile contamination */
            
            /* CRITICAL: Reset mobile body constraints for desktop */
            body {
                position: static !important;
                overflow: auto !important;
                width: 100% !important;
                height: auto !important;
                -webkit-overflow-scrolling: auto !important;
                overscroll-behavior: auto !important;
                touch-action: auto !important;
            }
            
            /* CRITICAL: Reset mobile game container constraints */
            #game-container {
                position: absolute !important;
                top: 50px !important; /* Height of top ribbon */
                left: 0 !important;
                width: 100% !important;
                height: calc(100% - 50px) !important;
                overflow: hidden !important; /* Desktop: hidden for games */
                -webkit-overflow-scrolling: auto !important;
                overscroll-behavior: auto !important;
                touch-action: auto !important;
                padding-bottom: 0 !important; /* Remove mobile bottom nav padding */
            }
            
            /* CRITICAL: Reset mobile canvas constraints */
            canvas {
                touch-action: auto !important;
                pointer-events: auto !important;
                transform: none !important;
                perspective: none !important;
            }
            
            /* CRITICAL: Ensure desktop top ribbon is visible */
            #top-ribbon {
                display: flex !important;
                visibility: visible !important;
                position: absolute !important;
                top: 0 !important;
                z-index: 1000 !important;
                height: 50px !important;
            }
            
            /* CRITICAL: Reset mobile overlay constraints */
            .mobile-overlay {
                display: none !important; /* Mobile overlays should not appear on desktop */
            }
            
            /* CRITICAL: Reset mobile-specific viewport units */
            [style*="100dvh"] {
                height: 100vh !important;
            }
            
            [style*="100dvw"] {
                width: 100vw !important;
            }
            
            /* CRITICAL: Reset mobile safe area constraints */
            [style*="safe-area-inset"] {
                padding-top: 0 !important;
                padding-bottom: 0 !important;
                padding-left: 0 !important;
                padding-right: 0 !important;
            }
            
            /* CRITICAL: Don't neutralize ALL mobile media queries - be more specific */
            /* Only neutralize problematic mobile styles, not authentication dialogs */
            
            /* Desktop-specific enhancements */
            .desktop-only {
                display: block !important;
            }
            
            .mobile-only {
                display: none !important;
            }
            
            /* Desktop auth dialog positioning - don't override auth dialog styles */
            /* AuthenticationDialogs.js handles its own auth-overlay styling */
            
            /* Desktop scrollable content reset */
            .scrollable-content {
                height: auto !important;
                overflow: visible !important;
                -webkit-overflow-scrolling: auto !important;
                overscroll-behavior-y: auto !important;
                padding-top: 0 !important;
                padding-bottom: 0 !important;
            }
        `;
        
        this.injectStyles('desktop-style-manager', desktopStyles);
        console.log('✅ Desktop styles applied');
    }

    /**
     * Setup monitoring for dynamic mobile style injection
     */
    setupStyleMonitoring() {
        // Monitor for new stylesheets being added
        this.styleObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeName === 'LINK' && node.rel === 'stylesheet') {
                        this.evaluateNewStylesheet(node);
                    } else if (node.nodeName === 'STYLE') {
                        this.evaluateNewStyleElement(node);
                    }
                });
            });
        });
        
        this.styleObserver.observe(document.head, {
            childList: true,
            subtree: true
        });
    }

    /**
     * Evaluate newly added stylesheets
     */
    evaluateNewStylesheet(linkElement) {
        const href = linkElement.href || '';
        const isMobileStyle = ['mobile-styles.css', 'unified-mobile.css'].some(file => href.includes(file));
        
        if (isMobileStyle && this.isDesktop) {
            console.log(`🚫 Blocking dynamically added mobile CSS: ${href}`);
            linkElement.media = 'screen and (max-width: 0px)'; // Never matches on desktop
            this.blockedMobileStyles.add(linkElement);
        }
    }

    /**
     * Evaluate newly added style elements
     */
    evaluateNewStyleElement(styleElement) {
        const content = styleElement.textContent || '';
        
        // Check for mobile-specific patterns
        const mobilePatterns = [
            '@media (max-width: 768px)',
            'touch-action: pan-y',
            '100dvh',
            'safe-area-inset',
            '-webkit-overflow-scrolling: touch'
        ];
        
        const containsMobileStyles = mobilePatterns.some(pattern => content.includes(pattern));
        
        if (containsMobileStyles && this.isDesktop) {
            console.log('🚫 Neutralizing dynamically added mobile styles');
            
            // Wrap mobile styles in a media query that never matches on desktop
            const neutralizedContent = `
                @media screen and (max-width: 0px) {
                    ${content}
                }
            `;
            
            styleElement.textContent = neutralizedContent;
        }
    }

    /**
     * Inject styles with proper scoping
     */
    injectStyles(id, css) {
        // Remove existing styles with same ID
        const existing = document.getElementById(id);
        if (existing) {
            existing.remove();
        }
        
        // Create and inject new styles
        const styleElement = document.createElement('style');
        styleElement.id = id;
        styleElement.textContent = css;
        document.head.appendChild(styleElement);
        
        this.appliedStyles.add(id);
    }

    /**
     * Force desktop mode - emergency override
     */
    forceDesktopMode() {
        console.log('🚨 Force desktop mode activated');
        
        // Remove all mobile styles
        this.blockedMobileStyles.forEach(stylesheet => {
            if (stylesheet.parentNode) {
                stylesheet.parentNode.removeChild(stylesheet);
            }
        });
        
        // Apply aggressive desktop overrides
        const emergencyDesktopStyles = `
            /* Emergency Desktop Mode - Override ALL mobile styles */
            * {
                touch-action: auto !important;
                -webkit-overflow-scrolling: auto !important;
                overscroll-behavior: auto !important;
            }
            
            html, body {
                position: static !important;
                overflow: auto !important;
                height: auto !important;
                width: 100% !important;
            }
            
            #game-container {
                position: absolute !important;
                top: 50px !important;
                overflow: hidden !important;
                height: calc(100% - 50px) !important;
            }
            
            .mobile-overlay,
            .mobile-optimized,
            .mobile-only {
                display: none !important;
            }
        `;
        
        this.injectStyles('emergency-desktop-mode', emergencyDesktopStyles);
        console.log('✅ Emergency desktop mode applied');
    }

    /**
     * Get desktop protection status
     */
    getProtectionStatus() {
        return {
            isDesktop: this.isDesktop,
            appliedStyles: Array.from(this.appliedStyles),
            blockedMobileStyles: this.blockedMobileStyles.size,
            monitoringActive: !!this.styleObserver
        };
    }

    /**
     * Cleanup resources
     */
    cleanup() {
        if (this.styleObserver) {
            this.styleObserver.disconnect();
            this.styleObserver = null;
        }
        
        // Remove applied styles
        this.appliedStyles.forEach(styleId => {
            const element = document.getElementById(styleId);
            if (element) {
                element.remove();
            }
        });
        
        this.appliedStyles.clear();
        this.blockedMobileStyles.clear();
        
        console.log('🧹 DesktopStyleManager cleaned up');
    }
}

// Create singleton instance
const desktopStyleManager = new DesktopStyleManager();

// Make globally available for debugging
window.desktopStyleManager = desktopStyleManager;

export default desktopStyleManager;
